import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Location } from '@up2stay/up2promo-api-client';
import { IntegrationsApi } from '@up2stay/up2promo-api-client';
import { Button } from '@up2stay/up2promo-ui';
import { Input } from '@up2stay/up2promo-ui';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@up2stay/up2promo-ui';
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from '@up2stay/up2promo-ui';
import { Loader2, Search, Star } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  isUnlimitedQuota,
  REPUTATION_LOCATION_ADDON_MONTHLY_EUR,
} from '@/core/authz/entitlement-catalog';
import { useFormatEur } from '@/core/billing/use-format-eur';
import {
  useEntitlements,
  useInvalidateWorkspaceEntitlements,
} from '@/core/hooks/use-entitlements';
import { useLocations } from '@/core/hooks/use-locations';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { useChannelLocations } from '@/core/hooks/use-reputation';
import {
  buildLocationNameById,
  GOOGLE_REVIEW_PROVIDER,
  type GoogleBusinessLocationOption,
  partitionGoogleLocations,
  partitionGoogleOptionsByAvailability,
  type ReviewProviderKey,
} from '@/core/lib/review-channel-setup';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';
import { locationsQueryKeys } from '@/core/query-keys/locations-query-keys';
import { integrationsQueryKeys } from '@/core/query-keys/integrations-query-keys';
import { reputationQueryKeys } from '@/core/query-keys/reputation-query-keys';

type Step = 'pick' | 'help';

type ConnectReviewProviderDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  location: Pick<
    Location,
    'id' | 'name' | 'address' | 'reviewExternalIds' | 'reviewChannelOptOut'
  >;
  provider: ReviewProviderKey;
  onSuccess?: () => void;
};

export function ConnectReviewProviderDialog({
  open,
  onOpenChange,
  workspaceId,
  location,
  provider,
  onSuccess,
}: ConnectReviewProviderDialogProps) {
  const dict = useDictionary();
  const d = dict.reputation?.setup?.connectDialog;
  const billingConfirmDict = dict.workspace.billing.reputationConnectConfirm;
  const formatEur = useFormatEur();
  const queryClient = useQueryClient();
  const { navigateShell } = useNavigateShell();
  const { usage, isFeatureEnabled } = useEntitlements();
  const invalidateEntitlements = useInvalidateWorkspaceEntitlements();
  const [step, setStep] = useState<Step>('pick');
  const [search, setSearch] = useState('');
  const [pendingLink, setPendingLink] =
    useState<GoogleBusinessLocationOption | null>(null);
  const [billingConfirmOpen, setBillingConfirmOpen] = useState(false);

  const reset = () => {
    setStep('pick');
    setSearch('');
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const invalidate = () => {
    void queryClient.invalidateQueries({
      queryKey: integrationsQueryKeys.root,
    });
    void queryClient.invalidateQueries({ queryKey: reputationQueryKeys.root });
    void queryClient.invalidateQueries({ queryKey: locationsQueryKeys.root });
    void queryClient.invalidateQueries({
      queryKey: locationsQueryKeys.detail(workspaceId, location.id),
    });
    invalidateEntitlements(workspaceId);
    onSuccess?.();
  };

  const { data: googleLocationsData, isLoading: googleLocationsLoading } =
    useQuery({
      queryKey: integrationsQueryKeys.googleLocations(workspaceId),
      queryFn: async () => {
        const res = await IntegrationsApi.listLocations({
          path: { workspaceId },
        });
        return (
          (
            res as {
              data?: { locations?: GoogleBusinessLocationOption[] };
            }
          )?.data ?? null
        );
      },
      enabled: open && provider === GOOGLE_REVIEW_PROVIDER,
      staleTime: 60_000,
    });

  const googleLocations = useMemo(
    () => googleLocationsData?.locations ?? [],
    [googleLocationsData?.locations],
  );

  const { data: channelLocations = [] } = useChannelLocations(workspaceId);
  const { locations: workspaceLocations } = useLocations(workspaceId, {
    limit: 100,
  });
  const locationNameById = useMemo(
    () => buildLocationNameById(workspaceLocations),
    [workspaceLocations],
  );

  const { suggested, other } = useMemo(
    () => partitionGoogleLocations(location, googleLocations, search),
    [location, googleLocations, search],
  );

  const partitionByAvailability = useCallback(
    (options: GoogleBusinessLocationOption[]) =>
      partitionGoogleOptionsByAvailability(
        options,
        location.id,
        channelLocations,
        locationNameById,
      ),
    [channelLocations, location.id, locationNameById],
  );

  const suggestedPartition = useMemo(
    () => partitionByAvailability(suggested),
    [partitionByAvailability, suggested],
  );
  const otherPartition = useMemo(
    () => partitionByAvailability(other),
    [partitionByAvailability, other],
  );

  const linkMutation = useMutation({
    mutationFn: async (option: GoogleBusinessLocationOption) => {
      await IntegrationsApi.link({
        path: { workspaceId, locationId: location.id },
        body: {
          placeId: option.placeId,
          googleBusinessLocationName: option.googleBusinessLocationName,
          title: option.title,
        },
      });
    },
    onSuccess: () => {
      invalidate();
      toast.success(d?.linkSuccess);
      handleOpenChange(false);
    },
    onError: (err: {
      response?: { data?: { errors?: Record<string, string> } };
    }) => {
      const errors = err?.response?.data?.errors;
      if (
        errors?.googleBusinessLocationName ===
          'alreadyLinkedToAnotherLocation' ||
        errors?.placeId === 'alreadyLinkedToAnotherLocation'
      ) {
        toast.error(d?.linkErrorAlreadyLinked);
        return;
      }
      toast.error(d?.linkError);
    },
  });

  const requestLink = (option: GoogleBusinessLocationOption) => {
    const rep = usage?.reputationLocations;
    const reputationEnabled = isFeatureEnabled('feature.reputation');
    if (!reputationEnabled || !rep) {
      linkMutation.mutate(option);
      return;
    }

    if (!isUnlimitedQuota(rep.included) && rep.connected >= rep.included) {
      setPendingLink(option);
      setBillingConfirmOpen(true);
      return;
    }

    linkMutation.mutate(option);
  };

  const confirmBillableLink = () => {
    if (!pendingLink) return;
    setBillingConfirmOpen(false);
    linkMutation.mutate(pendingLink);
    setPendingLink(null);
  };

  const skipMutation = useMutation({
    mutationFn: async () => {
      await IntegrationsApi.skip({
        path: { workspaceId, locationId: location.id },
      });
    },
    onSuccess: () => {
      invalidate();
      toast.success(d?.skipSuccess);
      handleOpenChange(false);
    },
    onError: () => {
      toast.error(d?.skipError);
    },
  });

  const title =
    step === 'pick'
      ? d?.pickTitle?.replace('{location}', location.name)
      : d?.helpTitle;

  const description = step === 'pick' ? d?.pickDescription : d?.helpDescription;

  return (
    <ResponsiveDialog open={open} onOpenChange={handleOpenChange}>
      <ResponsiveDialogContent className="sm:max-w-lg">
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>{title}</ResponsiveDialogTitle>
          {description ? (
            <ResponsiveDialogDescription>
              {description}
            </ResponsiveDialogDescription>
          ) : null}
        </ResponsiveDialogHeader>

        {step === 'pick' && (
          <div className="flex flex-col gap-3 py-2">
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={d?.searchPlaceholder}
                className="pl-9"
              />
            </div>

            {googleLocationsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : googleLocations.length === 0 ? (
              <p className="text-sm text-muted-foreground">{d?.emptyList}</p>
            ) : (
              <div className="flex flex-col gap-3 max-h-64 overflow-y-auto">
                {suggestedPartition.selectable.length > 0 ||
                suggestedPartition.taken.length > 0 ? (
                  <div className="flex flex-col gap-1">
                    <p className="text-xs font-medium text-muted-foreground">
                      {d?.suggestedLabel}
                    </p>
                    {suggestedPartition.selectable.map((option) => (
                      <LocationPickRow
                        key={option.googleBusinessLocationName}
                        option={option}
                        disabled={linkMutation.isPending}
                        onSelect={() => requestLink(option)}
                      />
                    ))}
                    {suggestedPartition.taken.map(
                      ({ option, linkedToLocationName }) => (
                        <LocationPickRow
                          key={option.googleBusinessLocationName}
                          option={option}
                          disabled
                          linkedToLocationName={linkedToLocationName}
                          linkedToLabel={d?.linkedToAnother}
                        />
                      ),
                    )}
                  </div>
                ) : null}
                {otherPartition.selectable.length > 0 ||
                otherPartition.taken.length > 0 ? (
                  <div className="flex flex-col gap-1">
                    {suggestedPartition.selectable.length > 0 ||
                    suggestedPartition.taken.length > 0 ? (
                      <p className="text-xs font-medium text-muted-foreground">
                        {d?.allLabel}
                      </p>
                    ) : null}
                    {otherPartition.selectable.map((option) => (
                      <LocationPickRow
                        key={option.googleBusinessLocationName}
                        option={option}
                        disabled={linkMutation.isPending}
                        onSelect={() => requestLink(option)}
                      />
                    ))}
                    {otherPartition.taken.map(
                      ({ option, linkedToLocationName }) => (
                        <LocationPickRow
                          key={option.googleBusinessLocationName}
                          option={option}
                          disabled
                          linkedToLocationName={linkedToLocationName}
                          linkedToLabel={d?.linkedToAnother}
                        />
                      ),
                    )}
                  </div>
                ) : null}
                {suggestedPartition.selectable.length === 0 &&
                  suggestedPartition.taken.length === 0 &&
                  otherPartition.selectable.length === 0 &&
                  otherPartition.taken.length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      {d?.noSearchResults}
                    </p>
                  )}
              </div>
            )}

            <button
              type="button"
              className="text-sm text-primary hover:underline text-left"
              onClick={() => setStep('help')}
            >
              {d?.cantFindLink}
            </button>
          </div>
        )}

        {step === 'pick' && (
          <ResponsiveDialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
            <Button
              type="button"
              variant="outline"
              className="w-full sm:w-auto"
              disabled={skipMutation.isPending || linkMutation.isPending}
              onClick={() => skipMutation.mutate()}
            >
              {d?.markNotUsing}
            </Button>
          </ResponsiveDialogFooter>
        )}

        {step === 'help' && (
          <div className="flex flex-col gap-3 py-2 text-sm text-muted-foreground">
            <p>{d?.helpBody}</p>
            <Button
              variant="outline"
              size="sm"
              className="w-fit"
              onClick={() =>
                navigateShell(shellPaths.moreWorkspaceIntegrations)
              }
            >
              {d?.openIntegrations}
            </Button>
          </div>
        )}

        {step === 'help' && (
          <ResponsiveDialogFooter>
            <Button variant="outline" onClick={() => setStep('pick')}>
              {d?.backToPick}
            </Button>
          </ResponsiveDialogFooter>
        )}
      </ResponsiveDialogContent>

      <AlertDialog
        open={billingConfirmOpen}
        onOpenChange={setBillingConfirmOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{billingConfirmDict.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {billingConfirmDict.description.replace(
                '{price}',
                formatEur(REPUTATION_LOCATION_ADDON_MONTHLY_EUR),
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingLink(null)}>
              {billingConfirmDict.cancel}
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmBillableLink}>
              {billingConfirmDict.confirm}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ResponsiveDialog>
  );
}

function LocationPickRow({
  option,
  onSelect,
  disabled,
  linkedToLocationName,
  linkedToLabel,
}: {
  option: GoogleBusinessLocationOption;
  onSelect?: () => void;
  disabled?: boolean;
  linkedToLocationName?: string;
  linkedToLabel?: string;
}) {
  const label =
    option.title ??
    option.googleBusinessLocationName.split('/').pop() ??
    option.placeId;

  const taken = Boolean(linkedToLocationName);
  const isDisabled = disabled || taken;

  return (
    <button
      type="button"
      disabled={isDisabled}
      onClick={taken ? undefined : onSelect}
      className="flex w-full items-center gap-2 rounded-md border px-3 py-2 text-left text-sm hover:bg-muted/50 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-transparent"
    >
      <Star className="h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {taken && linkedToLabel ? (
        <span className="shrink-0 text-xs text-muted-foreground">
          {linkedToLabel.replace('{location}', linkedToLocationName ?? '—')}
        </span>
      ) : null}
    </button>
  );
}
